import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import { CheckBoxWithLabelWidget, CompactInlineAlertWidget, InlineAlertType, TabStyles } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../codegen/Localize';
import { openReactActionDialog, ReactActionDialogBodyProps } from '../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { Subscriptions } from '../../dda/resources/Subscriptions';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ISubscriptionInfo } from '../../ts/models/ISubscriptionInfo';
import { SubscriptionDataConditionType } from '../../ts/ServerApi/types/SubscriptionDataCondition';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';


export const SUCCESS_TOAST_TEST_ID = 'change-subscription-data-condition-success';
const SUBSCRIPTION_DATA_CONDITION_TEST_ID = 'is-data-present';
export const SUBSCRIPTION_DATA_CONDITION_DIALOG_BODY_TEST_ID = 'is-data-present-dialog-body';

function changeDataCondition(subscription: ISubscriptionInfo): Promise<IResult> {
  const initialValue: boolean = subscription.dataConditionPresent;

  const dialogInstance = openReactActionDialog<ChangeSubscriptionDataConditionBodyProps, ChangeSubscriptionDataConditionBodyState, unknown>({
    title: Messages.confirmChangeSubscriptionDataCondition_Title(),
    bodyComponent: ChangeSubscriptionDataConditionBody,
    initialBodyProps: {
      id: subscription.id,
    },
    initialDialogState: {
      value: initialValue,
      errorMessage: undefined,
    },
    actionName: Messages.confirmChangeSubscriptionDataCondition(),
    actionDisabled: (props, state) => state.value === initialValue,
    action: changeDataConditionAction,
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: false
  });

  return Promise.resolve(dialogInstance.result);
}

export async function changeDataConditionAction(
  props: ChangeSubscriptionDataConditionBodyProps,
  dialogState: ChangeSubscriptionDataConditionBodyState,
  updateDialogState: React.Dispatch<MergeOrUpdateStateAction<ChangeSubscriptionDataConditionBodyState>>): Promise<IResult> {

  const newDataConditionValue: SubscriptionDataConditionType = dialogState.value ? 'DataPresent' : 'Always';
  const result = await Subscriptions.changeDataCondition([props.id], newDataConditionValue);

  return notify(result, updateDialogState);
}

export function notify(result: IResult, updateDialogState: React.Dispatch<MergeOrUpdateStateAction<ChangeSubscriptionDataConditionBodyState>>): Promise<IResult> {
  if (result.errors && result.errors.length) {
    updateDialogState({ errorMessage: Messages.changeSubscriptionDataConditionFailure() });
    return Promise.reject();
  } else {
    ToasterService.instance.pop({
      type: ToastType.Info,
      text: Messages.changeSubscriptionDataConditionSuccess(),
      testId: SUCCESS_TOAST_TEST_ID
    });
  }
  return Promise.resolve(result);
}

export type ChangeSubscriptionDataConditionBodyProps = {
  readonly id: string;
}

export type ChangeSubscriptionDataConditionBodyState = {
  readonly value: boolean;
  readonly errorMessage?: string;
}

export function ChangeSubscriptionDataConditionBody(
  props: ReactActionDialogBodyProps<ChangeSubscriptionDataConditionBodyProps, ChangeSubscriptionDataConditionBodyState>
): JSX.Element {

  const handleEnterKey = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
        props.doAction();
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.doAction]
  );

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <div
      className='tb-dialog-body-input'
      data-tb-test-id={SUBSCRIPTION_DATA_CONDITION_DIALOG_BODY_TEST_ID}
      onKeyDown={handleEnterKey}
    >
      <CheckBoxWithLabelWidget
        checked={props.dialogState.value}
        containerStyle={{ marginBottom: TabStyles.Spacing.LargeSpace }}
        label={Messages.confirmChangeSubscriptionDataConditionDescription()}
        handleChange={(checked: boolean) => { props.updateDialogState({ errorMessage: undefined, value: checked }); }}
        testId={SUBSCRIPTION_DATA_CONDITION_TEST_ID}
      />
      {props.dialogState.errorMessage &&
        <CompactInlineAlertWidget
          testId={SUBSCRIPTION_DATA_CONDITION_TEST_ID+'-error'}
          type={InlineAlertType.Error}
        >
          {props.dialogState.errorMessage}
        </CompactInlineAlertWidget>
      }
    </div>
  );
}

export const ChangeSubscriptionDataConditionAction = {
  changeDataCondition
};
